import { useEffect, useState, useRef } from 'react';
import { apiRequest } from '../utils/auth';
import { useEscapeToClose } from '../utils/useEscapeToClose';

interface BoostedCreator {
  _id: string;
  userId: string;
  name: string;
  order: number;
  addedBy?: string;
  createdAt: string;
}

interface BoostedExtension {
  _id: string;
  extensionId: string;
  name: string;
  thumbnailImageUrl?: string;
  order: number;
  addedBy?: string;
  createdAt: string;
}

interface UserSearchResult {
  userId: string;
  name: string;
  email?: string;
  avatar?: string;
}

interface ExtensionSearchResult {
  _id: string;
  Name: string;
  Creator: string;
  thumbnailImageUrl?: string;
}

export default function BoostPage() {
  // --- Creators state ---
  const [creators, setCreators] = useState<BoostedCreator[]>([]);
  const [creatorsLoading, setCreatorsLoading] = useState(true);
  const [creatorsError, setCreatorsError] = useState<string | null>(null);
  const [creatorProcessingId, setCreatorProcessingId] = useState<string | null>(null);
  const [showCreatorModal, setShowCreatorModal] = useState(false);
  const [creatorSearch, setCreatorSearch] = useState('');
  const [creatorSearchResults, setCreatorSearchResults] = useState<UserSearchResult[]>([]);
  const [creatorSearching, setCreatorSearching] = useState(false);
  const [addingCreator, setAddingCreator] = useState<string | null>(null);
  const creatorSearchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const creatorDragItem = useRef<number | null>(null);
  const creatorDragOver = useRef<number | null>(null);

  // --- Extensions state ---
  const [extensions, setExtensions] = useState<BoostedExtension[]>([]);
  const [extensionsLoading, setExtensionsLoading] = useState(true);
  const [extensionsError, setExtensionsError] = useState<string | null>(null);
  const [extProcessingId, setExtProcessingId] = useState<string | null>(null);
  const [showExtModal, setShowExtModal] = useState(false);
  const [extSearch, setExtSearch] = useState('');
  const [extSearchResults, setExtSearchResults] = useState<ExtensionSearchResult[]>([]);
  const [extSearching, setExtSearching] = useState(false);
  const [addingExt, setAddingExt] = useState<string | null>(null);
  const extSearchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const extDragItem = useRef<number | null>(null);
  const extDragOver = useRef<number | null>(null);

  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Closing a search dialog drops the query with it, so Escape, the backdrop
  // click and the Cancel button all share one handler per dialog.
  const closeCreatorModal = () => { setShowCreatorModal(false); setCreatorSearch(''); setCreatorSearchResults([]); };
  const closeExtModal = () => { setShowExtModal(false); setExtSearch(''); setExtSearchResults([]); };
  useEscapeToClose(showCreatorModal, closeCreatorModal);
  useEscapeToClose(showExtModal, closeExtModal);

  // --- Fetch ---
  const fetchCreators = async () => {
    try {
      setCreatorsLoading(true);
      setCreatorsError(null);
      const res = await apiRequest('/api/admin/boosted-creators');
      setCreators(res.data || []);
    } catch (err: any) {
      setCreatorsError(err.message || 'Failed to fetch');
    } finally {
      setCreatorsLoading(false);
    }
  };

  const fetchExtensions = async () => {
    try {
      setExtensionsLoading(true);
      setExtensionsError(null);
      const res = await apiRequest('/api/admin/boosted-extensions');
      setExtensions(res.data || []);
    } catch (err: any) {
      setExtensionsError(err.message || 'Failed to fetch');
    } finally {
      setExtensionsLoading(false);
    }
  };

  useEffect(() => {
    fetchCreators();
    fetchExtensions();
  }, []);

  // --- Creator handlers ---
  const handleCreatorSearchChange = (value: string) => {
    setCreatorSearch(value);
    if (creatorSearchTimeout.current) clearTimeout(creatorSearchTimeout.current);
    if (value.trim().length < 2) { setCreatorSearchResults([]); return; }
    creatorSearchTimeout.current = setTimeout(async () => {
      try {
        setCreatorSearching(true);
        const res = await apiRequest(`/api/admin/notifications/search-users?q=${encodeURIComponent(value.trim())}`);
        setCreatorSearchResults(res.data || []);
      } catch { /* ignore */ } finally { setCreatorSearching(false); }
    }, 300);
  };

  const handleAddCreator = async (user: UserSearchResult) => {
    if (creators.some(c => c.userId === user.userId)) { alert('Already in list'); return; }
    try {
      setAddingCreator(user.userId);
      await apiRequest('/api/admin/boosted-creators', { method: 'POST', body: JSON.stringify({ userId: user.userId }) });
      setShowCreatorModal(false); setCreatorSearch(''); setCreatorSearchResults([]);
      await fetchCreators();
    } catch (err: any) { alert(err.message || 'Failed'); } finally { setAddingCreator(null); }
  };

  const handleRemoveCreator = async (userId: string) => {
    if (!window.confirm('Remove this creator from the boost list?')) return;
    try {
      setCreatorProcessingId(userId);
      await apiRequest(`/api/admin/boosted-creators/${userId}`, { method: 'DELETE' });
      await fetchCreators();
    } catch (err: any) { alert(err.message || 'Failed'); } finally { setCreatorProcessingId(null); }
  };

  const creatorDragEnd = async () => {
    if (creatorDragItem.current === null || creatorDragOver.current === null || creatorDragItem.current === creatorDragOver.current) {
      creatorDragItem.current = null; creatorDragOver.current = null; return;
    }
    const reordered = [...creators];
    const [removed] = reordered.splice(creatorDragItem.current, 1);
    reordered.splice(creatorDragOver.current, 0, removed);
    creatorDragItem.current = null; creatorDragOver.current = null;
    setCreators(reordered);
    try {
      await apiRequest('/api/admin/boosted-creators/reorder', { method: 'POST', body: JSON.stringify({ userIds: reordered.map(c => c.userId) }) });
    } catch { alert('Failed to save order'); await fetchCreators(); }
  };

  const moveCreator = async (index: number, dir: -1 | 1) => {
    const ni = index + dir;
    if (ni < 0 || ni >= creators.length) return;
    const reordered = [...creators];
    [reordered[index], reordered[ni]] = [reordered[ni], reordered[index]];
    setCreators(reordered);
    try {
      await apiRequest('/api/admin/boosted-creators/reorder', { method: 'POST', body: JSON.stringify({ userIds: reordered.map(c => c.userId) }) });
    } catch { await fetchCreators(); }
  };

  // --- Extension handlers ---
  const handleExtSearchChange = (value: string) => {
    setExtSearch(value);
    if (extSearchTimeout.current) clearTimeout(extSearchTimeout.current);
    if (value.trim().length < 2) { setExtSearchResults([]); return; }
    extSearchTimeout.current = setTimeout(async () => {
      try {
        setExtSearching(true);
        const res = await apiRequest(`/api/admin/notifications/search-extensions?q=${encodeURIComponent(value.trim())}`);
        setExtSearchResults(res.data || []);
      } catch { /* ignore */ } finally { setExtSearching(false); }
    }, 300);
  };

  const handleAddExtension = async (ext: ExtensionSearchResult) => {
    if (extensions.some(e => e.extensionId === ext._id)) { alert('Already in list'); return; }
    try {
      setAddingExt(ext._id);
      await apiRequest('/api/admin/boosted-extensions', { method: 'POST', body: JSON.stringify({ extensionId: ext._id }) });
      setShowExtModal(false); setExtSearch(''); setExtSearchResults([]);
      await fetchExtensions();
    } catch (err: any) { alert(err.message || 'Failed'); } finally { setAddingExt(null); }
  };

  const handleRemoveExtension = async (extensionId: string) => {
    if (!window.confirm('Remove this lens from the boost list?')) return;
    try {
      setExtProcessingId(extensionId);
      await apiRequest(`/api/admin/boosted-extensions/${extensionId}`, { method: 'DELETE' });
      await fetchExtensions();
    } catch (err: any) { alert(err.message || 'Failed'); } finally { setExtProcessingId(null); }
  };

  const extDragEnd2 = async () => {
    if (extDragItem.current === null || extDragOver.current === null || extDragItem.current === extDragOver.current) {
      extDragItem.current = null; extDragOver.current = null; return;
    }
    const reordered = [...extensions];
    const [removed] = reordered.splice(extDragItem.current, 1);
    reordered.splice(extDragOver.current, 0, removed);
    extDragItem.current = null; extDragOver.current = null;
    setExtensions(reordered);
    try {
      await apiRequest('/api/admin/boosted-extensions/reorder', { method: 'POST', body: JSON.stringify({ extensionIds: reordered.map(e => e.extensionId) }) });
    } catch { alert('Failed to save order'); await fetchExtensions(); }
  };

  const moveExtension = async (index: number, dir: -1 | 1) => {
    const ni = index + dir;
    if (ni < 0 || ni >= extensions.length) return;
    const reordered = [...extensions];
    [reordered[index], reordered[ni]] = [reordered[ni], reordered[index]];
    setExtensions(reordered);
    try {
      await apiRequest('/api/admin/boosted-extensions/reorder', { method: 'POST', body: JSON.stringify({ extensionIds: reordered.map(e => e.extensionId) }) });
    } catch { await fetchExtensions(); }
  };

  // --- Render ---
  const sectionHeader = { display: 'flex', flexWrap: 'wrap' as const, gap: '12px', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' } as const;
  const cardStyle = { backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column' as const, gap: '10px' } as const;
  const arrowBtn = (disabled: boolean) => ({ padding: '4px 12px', backgroundColor: 'transparent', color: disabled ? 'var(--color-border-strong)' : 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: '4px', cursor: disabled ? 'default' : 'pointer', fontSize: '14px' });
  const removeBtn = (busy: boolean) => ({ padding: '6px 14px', backgroundColor: 'var(--color-danger-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1, fontSize: '13px' });
  const addBtn = { padding: '8px 20px', backgroundColor: 'var(--color-success-strong)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' } as const;
  const tableWrap = { backgroundColor: 'var(--color-surface)', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' } as const;
  const thStyle = { padding: '12px', textAlign: 'left' as const, color: 'var(--color-text)' };
  const thCenter = { ...thStyle, textAlign: 'center' as const };

  return (
    <div className="page">
      {/* ===== BOOSTED CREATORS ===== */}
      <div style={sectionHeader}>
        <h1 style={{ margin: 0 }}>Boosted Creators</h1>
        <button onClick={() => setShowCreatorModal(true)} style={addBtn}>+ Add Creator</button>
      </div>
      <p style={{ color: 'var(--color-muted)', marginBottom: '20px' }}>
        Creators in this list get a ranking boost. Drag rows or use arrows to reorder — #1 gets the highest boost.
      </p>

      {creatorsLoading ? <p>Loading...</p> : creatorsError ? (
        <p style={{ color: 'var(--color-danger)' }}>Error: {creatorsError} <button onClick={fetchCreators}>Retry</button></p>
      ) : creators.length === 0 ? (
        <p style={{ color: 'var(--color-muted)' }}>No boosted creators yet.</p>
      ) : isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {creators.map((c, i) => (
            <div key={c._id} style={cardStyle}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: 'var(--color-text)', fontWeight: 600, fontSize: '15px' }}>
                    <span style={{ color: 'var(--color-muted)', marginRight: '6px' }}>#{i + 1}</span>{c.name}
                  </div>
                  <div style={{ color: 'var(--color-muted)', fontSize: '12px' }}>Added by {c.addedBy || '-'}</div>
                </div>
                <button onClick={() => handleRemoveCreator(c.userId)} disabled={creatorProcessingId === c.userId} style={removeBtn(creatorProcessingId === c.userId)}>
                  {creatorProcessingId === c.userId ? '...' : 'Remove'}
                </button>
              </div>
              <div style={{ display: 'flex', gap: '8px', borderTop: '1px solid var(--color-border)', paddingTop: '10px' }}>
                <button onClick={() => moveCreator(i, -1)} disabled={i === 0} style={{ ...arrowBtn(i === 0), flex: 1 }}>▲ Up</button>
                <button onClick={() => moveCreator(i, 1)} disabled={i === creators.length - 1} style={{ ...arrowBtn(i === creators.length - 1), flex: 1 }}>▼ Down</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={tableWrap}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--color-surface-hover)', borderBottom: '2px solid var(--color-border)' }}>
                <th style={{ ...thCenter, width: '50px' }}>#</th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Added By</th>
                <th style={{ ...thCenter, width: '100px' }}>Order</th>
                <th style={{ ...thCenter, width: '100px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {creators.map((c, i) => (
                <tr key={c._id} draggable onDragStart={() => { creatorDragItem.current = i; }}
                  onDragEnter={() => { creatorDragOver.current = i; }} onDragEnd={creatorDragEnd}
                  onDragOver={e => e.preventDefault()} style={{ borderBottom: '1px solid var(--color-border)', cursor: 'grab' }}>
                  <td style={{ padding: '12px', textAlign: 'center', color: 'var(--color-muted)' }}>☰</td>
                  <td style={{ padding: '12px', color: 'var(--color-text)', fontWeight: 500 }}>{c.name}</td>
                  <td style={{ padding: '12px', color: 'var(--color-muted)' }}>{c.addedBy || '-'}</td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button onClick={() => moveCreator(i, -1)} disabled={i === 0}
                      style={{ padding: '2px 8px', marginRight: '4px', backgroundColor: 'transparent', color: i === 0 ? 'var(--color-border-strong)' : 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: '4px', cursor: i === 0 ? 'default' : 'pointer' }}>▲</button>
                    <button onClick={() => moveCreator(i, 1)} disabled={i === creators.length - 1}
                      style={{ padding: '2px 8px', backgroundColor: 'transparent', color: i === creators.length - 1 ? 'var(--color-border-strong)' : 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: '4px', cursor: i === creators.length - 1 ? 'default' : 'pointer' }}>▼</button>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button onClick={() => handleRemoveCreator(c.userId)} disabled={creatorProcessingId === c.userId}
                      style={{ padding: '6px 12px', backgroundColor: 'var(--color-danger-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: creatorProcessingId === c.userId ? 'not-allowed' : 'pointer', opacity: creatorProcessingId === c.userId ? 0.6 : 1 }}>
                      {creatorProcessingId === c.userId ? '...' : 'Remove'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Creator Search Modal */}
      {showCreatorModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={closeCreatorModal}>
          <div style={{ backgroundColor: 'var(--color-surface-secondary)', borderRadius: '12px', width: '500px', maxHeight: '70vh', display: 'flex', flexDirection: 'column', border: '1px solid var(--color-border)', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--color-border)' }}>
              <h2 style={{ margin: '0 0 12px 0', color: 'var(--color-text)', fontSize: '18px' }}>Add Boosted Creator</h2>
              <input type="text" autoFocus value={creatorSearch} onChange={e => handleCreatorSearchChange(e.target.value)}
                placeholder="Search by name, email, or user ID..."
                style={{ width: '100%', padding: '10px 14px', backgroundColor: 'var(--color-input)', border: '1px solid var(--color-border)', borderRadius: '6px', color: 'var(--color-text)', fontSize: '14px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ overflowY: 'auto', maxHeight: '400px' }}>
              {creatorSearching && <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-muted)' }}>Searching...</div>}
              {!creatorSearching && creatorSearch.trim().length >= 2 && creatorSearchResults.length === 0 && (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-muted)' }}>No users found</div>
              )}
              {!creatorSearching && creatorSearch.trim().length < 2 && (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-muted)' }}>Type at least 2 characters to search</div>
              )}
              {creatorSearchResults.map(user => {
                const alreadyAdded = creators.some(c => c.userId === user.userId);
                return (
                  <div key={user.userId} style={{ padding: '12px 20px', borderBottom: '1px solid var(--color-surface-hover)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                      {user.avatar ? (
                        <img src={user.avatar} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: 'var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-muted)', fontSize: '14px' }}>
                          {(user.name || '?')[0].toUpperCase()}
                        </div>
                      )}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ color: 'var(--color-text)', fontWeight: 500 }}>{user.name}</div>
                        <div style={{ color: 'var(--color-muted)', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email || user.userId}</div>
                      </div>
                    </div>
                    <button onClick={() => handleAddCreator(user)} disabled={alreadyAdded || addingCreator === user.userId}
                      style={{ padding: '6px 16px', marginLeft: '12px', flexShrink: 0, backgroundColor: alreadyAdded ? 'var(--color-border)' : 'var(--color-success-strong)', color: alreadyAdded ? 'var(--color-muted)' : 'white', border: 'none', borderRadius: '4px', fontSize: '13px', cursor: alreadyAdded || addingCreator === user.userId ? 'default' : 'pointer', opacity: addingCreator === user.userId ? 0.6 : 1 }}>
                      {alreadyAdded ? 'Added' : addingCreator === user.userId ? 'Adding...' : 'Add'}
                    </button>
                  </div>
                );
              })}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--color-border)', textAlign: 'right' }}>
              <button onClick={closeCreatorModal}
                style={{ padding: '8px 16px', backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== BOOSTED EXTENSIONS ===== */}
      <div style={{ ...sectionHeader, marginTop: '48px' }}>
        <h1 style={{ margin: 0 }}>Boosted Lenses</h1>
        <button onClick={() => setShowExtModal(true)} style={addBtn}>+ Add Lens</button>
      </div>
      <p style={{ color: 'var(--color-muted)', marginBottom: '20px' }}>
        Lenses in this list get a ranking boost. Drag rows or use arrows to reorder — #1 gets the highest boost.
      </p>

      {extensionsLoading ? <p>Loading...</p> : extensionsError ? (
        <p style={{ color: 'var(--color-danger)' }}>Error: {extensionsError} <button onClick={fetchExtensions}>Retry</button></p>
      ) : extensions.length === 0 ? (
        <p style={{ color: 'var(--color-muted)' }}>No boosted lenses yet.</p>
      ) : isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {extensions.map((ext, i) => (
            <div key={ext._id} style={cardStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {ext.thumbnailImageUrl ? (
                  <img src={ext.thumbnailImageUrl} alt="" style={{ width: 48, height: 48, borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 48, height: 48, borderRadius: '8px', backgroundColor: 'var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-muted)', fontSize: '12px', flexShrink: 0 }}>N/A</div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: 'var(--color-text)', fontWeight: 600, fontSize: '15px' }}>
                    <span style={{ color: 'var(--color-muted)', marginRight: '6px' }}>#{i + 1}</span>{ext.name}
                  </div>
                  <div style={{ color: 'var(--color-muted)', fontSize: '12px', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ext.extensionId}</div>
                  <div style={{ color: 'var(--color-muted)', fontSize: '12px' }}>Added by {ext.addedBy || '-'}</div>
                </div>
                <button onClick={() => handleRemoveExtension(ext.extensionId)} disabled={extProcessingId === ext.extensionId} style={{ ...removeBtn(extProcessingId === ext.extensionId), flexShrink: 0 }}>
                  {extProcessingId === ext.extensionId ? '...' : 'Remove'}
                </button>
              </div>
              <div style={{ display: 'flex', gap: '8px', borderTop: '1px solid var(--color-border)', paddingTop: '10px' }}>
                <button onClick={() => moveExtension(i, -1)} disabled={i === 0} style={{ ...arrowBtn(i === 0), flex: 1 }}>▲ Up</button>
                <button onClick={() => moveExtension(i, 1)} disabled={i === extensions.length - 1} style={{ ...arrowBtn(i === extensions.length - 1), flex: 1 }}>▼ Down</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={tableWrap}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--color-surface-hover)', borderBottom: '2px solid var(--color-border)' }}>
                <th style={{ ...thCenter, width: '50px' }}>#</th>
                <th style={{ ...thCenter, width: '60px' }}>Thumb</th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Lens ID</th>
                <th style={thStyle}>Added By</th>
                <th style={{ ...thCenter, width: '100px' }}>Order</th>
                <th style={{ ...thCenter, width: '100px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {extensions.map((ext, i) => (
                <tr key={ext._id} draggable onDragStart={() => { extDragItem.current = i; }}
                  onDragEnter={() => { extDragOver.current = i; }} onDragEnd={extDragEnd2}
                  onDragOver={e => e.preventDefault()} style={{ borderBottom: '1px solid var(--color-border)', cursor: 'grab' }}>
                  <td style={{ padding: '12px', textAlign: 'center', color: 'var(--color-muted)' }}>☰</td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    {ext.thumbnailImageUrl ? (
                      <img src={ext.thumbnailImageUrl} alt="" style={{ width: 40, height: 40, borderRadius: '6px', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: 40, height: 40, borderRadius: '6px', backgroundColor: 'var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-muted)', fontSize: '12px', margin: '0 auto' }}>N/A</div>
                    )}
                  </td>
                  <td style={{ padding: '12px', color: 'var(--color-text)', fontWeight: 500 }}>{ext.name}</td>
                  <td style={{ padding: '12px', color: 'var(--color-muted)', fontSize: '13px', fontFamily: 'monospace' }}>{ext.extensionId}</td>
                  <td style={{ padding: '12px', color: 'var(--color-muted)' }}>{ext.addedBy || '-'}</td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button onClick={() => moveExtension(i, -1)} disabled={i === 0}
                      style={{ padding: '2px 8px', marginRight: '4px', backgroundColor: 'transparent', color: i === 0 ? 'var(--color-border-strong)' : 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: '4px', cursor: i === 0 ? 'default' : 'pointer' }}>▲</button>
                    <button onClick={() => moveExtension(i, 1)} disabled={i === extensions.length - 1}
                      style={{ padding: '2px 8px', backgroundColor: 'transparent', color: i === extensions.length - 1 ? 'var(--color-border-strong)' : 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: '4px', cursor: i === extensions.length - 1 ? 'default' : 'pointer' }}>▼</button>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button onClick={() => handleRemoveExtension(ext.extensionId)} disabled={extProcessingId === ext.extensionId}
                      style={{ padding: '6px 12px', backgroundColor: 'var(--color-danger-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: extProcessingId === ext.extensionId ? 'not-allowed' : 'pointer', opacity: extProcessingId === ext.extensionId ? 0.6 : 1 }}>
                      {extProcessingId === ext.extensionId ? '...' : 'Remove'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Extension Search Modal */}
      {showExtModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={closeExtModal}>
          <div style={{ backgroundColor: 'var(--color-surface-secondary)', borderRadius: '12px', width: '500px', maxHeight: '70vh', display: 'flex', flexDirection: 'column', border: '1px solid var(--color-border)', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--color-border)' }}>
              <h2 style={{ margin: '0 0 12px 0', color: 'var(--color-text)', fontSize: '18px' }}>Add Boosted Lens</h2>
              <input type="text" autoFocus value={extSearch} onChange={e => handleExtSearchChange(e.target.value)}
                placeholder="Search lenses by name..."
                style={{ width: '100%', padding: '10px 14px', backgroundColor: 'var(--color-input)', border: '1px solid var(--color-border)', borderRadius: '6px', color: 'var(--color-text)', fontSize: '14px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ overflowY: 'auto', maxHeight: '400px' }}>
              {extSearching && <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-muted)' }}>Searching...</div>}
              {!extSearching && extSearch.trim().length >= 2 && extSearchResults.length === 0 && (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-muted)' }}>No lenses found</div>
              )}
              {!extSearching && extSearch.trim().length < 2 && (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-muted)' }}>Type at least 2 characters to search</div>
              )}
              {extSearchResults.map(ext => {
                const alreadyAdded = extensions.some(e => e.extensionId === ext._id);
                return (
                  <div key={ext._id} style={{ padding: '12px 20px', borderBottom: '1px solid var(--color-surface-hover)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                      {ext.thumbnailImageUrl ? (
                        <img src={ext.thumbnailImageUrl} alt="" style={{ width: 40, height: 40, borderRadius: '6px', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: 40, height: 40, borderRadius: '6px', backgroundColor: 'var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-muted)', fontSize: '12px' }}>N/A</div>
                      )}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ color: 'var(--color-text)', fontWeight: 500 }}>{ext.Name}</div>
                        <div style={{ color: 'var(--color-muted)', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>by {ext.Creator}</div>
                      </div>
                    </div>
                    <button onClick={() => handleAddExtension(ext)} disabled={alreadyAdded || addingExt === ext._id}
                      style={{ padding: '6px 16px', marginLeft: '12px', flexShrink: 0, backgroundColor: alreadyAdded ? 'var(--color-border)' : 'var(--color-success-strong)', color: alreadyAdded ? 'var(--color-muted)' : 'white', border: 'none', borderRadius: '4px', fontSize: '13px', cursor: alreadyAdded || addingExt === ext._id ? 'default' : 'pointer', opacity: addingExt === ext._id ? 0.6 : 1 }}>
                      {alreadyAdded ? 'Added' : addingExt === ext._id ? 'Adding...' : 'Add'}
                    </button>
                  </div>
                );
              })}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--color-border)', textAlign: 'right' }}>
              <button onClick={closeExtModal}
                style={{ padding: '8px 16px', backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}